import { Component, OnInit, Optional, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import {
  CustomTranslationService,
  CustomLanguage,
  TranslationObj,
  TranslationItem,
  Bundle,
  NotCreatedTranslationItem
} from '@app/shared/services/custom-translation.service';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/components/common/messageservice';

@Component({
  selector: 'app-custom-translation-dialog',
  templateUrl: './custom-translation-dialog.component.html',
  styleUrls: ['./custom-translation-dialog.component.scss']
})
export class CustomTranslationDialogComponent implements OnInit {
  form: FormGroup;

  translationObj: TranslationObj;
  listOfBundles: Bundle[] = [];

  editing = false;

  private messages = '';
  private translate: any;

  constructor(
    translate: TranslateService,
    public dialogRef: MatDialogRef<CustomTranslationDialogComponent>,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      value: string;
      fromSettings?: { label: string; rules: string };
      bundleNames?: string[];
      notLockedValue?: string;
    },
    private fb: FormBuilder,
    private customTranslationService: CustomTranslationService,
    private messageService: MessageService
  ) {
    this.customTranslationService.getListOfBundles().subscribe(bundles => {
      this.filterBundles(bundles, this.data.bundleNames, this.data.fromSettings);
      this.createForm();
    });
    this.translate = translate;
  }

  get translationFormArray(): FormArray {
    return this.form.controls.translations as FormArray;
  }

  ngOnInit() {}

  save(): void {
    if (this.editing) {
      // update translations
      this.updateTranslationsReq().subscribe((results: any[]) => {
        this.updateLocalTranslationObj(results);
        this.customTranslationService.updateTranslations(this.translationObj);
        this.dialogRef.close(this.translationObj);
      });
    } else {
      // create translations
      this.createTranslationReq().subscribe((saveResult: TranslationObj) => {
        this.customTranslationService.translations.push(saveResult);
        this.dialogRef.close(saveResult);
      });
    }
  }

  validate(index: number): void {
    if (this.data.fromSettings) {
      const value = this.translationFormArray.value[index].value;
      const key = this.data.fromSettings.label;
      const validationRules = this.data.fromSettings.rules.split('|');
      this.messages = '';
      if (validationRules.includes('required') && value.length == 0) {
        this.messages = this.translate.get(key).value + ': ' + this.translate.get('VALUE_REQUIRED').value;
      } else if (validationRules.includes('integer') && Number.isInteger(value)) {
        this.messages =
          this.translate.get(key).value + ': ' + this.translate.get('SETTINGS_VALUE_MUST_BE').value + ' integer. ';
      } else if (validationRules.includes('numeric') && isNaN(parseFloat(value))) {
        this.messages =
          this.translate.get(key).value + ': ' + this.translate.get('SETTINGS_VALUE_MUST_BE').value + ' numeric. ';
      } else if (validationRules.includes('string') && typeof value != 'string') {
        this.messages =
          this.translate.get(key).value + ': ' + this.translate.get('SETTINGS_VALUE_MUST_BE').value + ' string. ';
      }

      let getCompareVal = val => {
        if (validationRules.includes('string')) {
          return (val || '').length;
        } else {
          return parseFloat(val);
        }
      };

      if (validationRules.findIndex(maxvalue => /^max:/.test(maxvalue)) > -1) {
        let onPosition = validationRules.findIndex(maxvalue => /^max:/.test(maxvalue));
        let maxRule = validationRules[onPosition].split(':')[1];
        if (getCompareVal(value) > parseFloat(maxRule)) {
          this.messages =
            this.messages +
            ' ' +
            this.translate.get(key).value +
            ': ' +
            this.translate.get('SETTINGS_VALUE_MUST_BE_LESS_OR_EQUAL').value +
            maxRule +
            '.';
        }
      }

      if (validationRules.findIndex(minvalue => /^min:/.test(minvalue)) > -1) {
        let onPosition = validationRules.findIndex(minvalue => /^min:/.test(minvalue));
        let minRule = validationRules[onPosition].split(':')[1];
        if (getCompareVal(value) < parseFloat(minRule)) {
          this.messages =
            this.messages +
            ' ' +
            this.translate.get(key).value +
            ': ' +
            this.translate.get('SETTINGS_VALUE_MUST_BE_MORE_OR_EQUAL').value +
            ' ' +
            minRule +
            '.';
        }
      }

      if (validationRules.findIndex(minvalue => /^in:/.test(minvalue)) > -1) {
        let onPosition = validationRules.findIndex(minvalue => /^in:/.test(minvalue));
        let enumInStr = validationRules[onPosition].split(':')[1];
        let enumIn = enumInStr.split(',');
        if (enumIn.indexOf(value) < 0) {
          this.messages =
            this.messages +
            ' ' +
            this.translate.get(key).value +
            ': ' +
            this.translate.get('SETTINGS_VALUE_MUST_BE_IN_DIAPASON').value +
            ' "' +
            enumInStr +
            '".';
        }
      }

      if (this.messages.length !== 0) {
        this.showViaService('error', 'Error Message', this.messages);
      }
    }
  }

  private filterBundles(bundles, bundleFilterNames: string[], fromSettings: { label: string; rules: string }) {
    let bundleNames = [];

    if (fromSettings && fromSettings.rules) {
      const rules: string[] = fromSettings.rules.split('|');
      rules.forEach(rule => {
        const splitedRule = rule.split(':');
        if (splitedRule[0] === 'loc_bundle_type') {
          bundleNames = splitedRule[1].split(',');
        }
      });
    } else {
      bundleNames = bundleFilterNames;
    }

    if (bundleNames && bundleNames.length > 0) {
      this.listOfBundles = bundles.filter(bundle => {
        return this.hasBundleByName(bundleNames, bundle.name);
      });
    } else {
      this.listOfBundles = bundles;
    }
  }

  private hasBundleByName(bundleNames: string[], bundleName: string): boolean {
    return bundleNames.some(bName => bName === bundleName);
  }

  private createTranslationReq(): Observable<any> {
    const values: NotCreatedTranslationItem[] = this.translationFormArray.value.map(itemValue => {
      return {
        bundle_ids: this.form.value.bundles,
        locale_id: itemValue.locId,
        value: itemValue.value
      };
    });

    return this.customTranslationService.postTranslation(values);
  }

  private updateTranslationsReq(): Observable<Array<{ id: string; value: string; bundle_ids: string[] }>> {
    const reqs: Array<Observable<{
      id: string;
      value: string;
      bundle_ids: string[];
    }>> = this.translationFormArray.value.map(itemValue => {
      return this.customTranslationService.putTranslation({
        id: itemValue.id,
        value: itemValue.value,
        bundle_ids: this.form.value.bundles
      });
    });

    return forkJoin(reqs);
  }

  private showViaService(result, title, text) {
    this.messageService.add({ severity: result, summary: title, detail: text });
  }

  private createForm(): void {
    this.form = this.fb.group({
      translations: this.fb.array([]),
      bundles: [this.listOfBundles.map(bund => bund.id), [Validators.required]]
    });

    if (this.data.value) {
      this.translationObj = this.customTranslationService.translations.find(
        transObj => transObj.key === this.data.value
      );

      if (this.translationObj) {
        this.form.controls.bundles.setValue(this.translationObj.translations[0].bundle_ids);
      }
    }

    this.customTranslationService.langs.forEach((lang: CustomLanguage) => {
      const value: { id: string; value: string } = this.getValueByLangOfTransObj(lang.id);
      if (value.id) {
        this.editing = true;
      }
      this.translationFormArray.push(
        this.fb.group({
          lang: [lang.name],
          value: [value.value, [Validators.required]],
          locId: [lang.id],
          id: [value.id]
        })
      );
    });
  }

  private updateLocalTranslationObj(newTransValues): void {
    this.translationObj.translations = this.translationObj.translations.map((transItem: TranslationItem) => {
      return {
        ...transItem,
        value: newTransValues.find(newVal => newVal.id === transItem.id).value,
        bundle_ids: newTransValues[0].bundle_ids
      };
    });
  }

  private getValueByLangOfTransObj(langId: string): { id: string; value: string } {
    let value = { id: '', value: this.data.notLockedValue ? this.data.notLockedValue : '' };
    let translateItem: TranslationItem;

    if (this.translationObj) {
      translateItem = this.translationObj.translations.find(trans => trans.locale_id === langId);
      if (translateItem) {
        value = {
          value: translateItem.value,
          id: translateItem.id
        };
      }
    }

    return value;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/custom-translation-dialog/custom-translation-dialog.component.ts